import { useState } from "react";
import {
  ApiError,
  apiFetch,
  type PortalDocumentSignRequest,
  type PortalSignableDocument,
} from "@dearbill/shared";
import { documentsApiPath } from "./documents";
import type { SignatureInput } from "./SignatureForm";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

export interface SubmitNotice {
  tone: "error" | "warning";
  title: string;
  body: string;
}

interface UseDocumentSubmitArgs {
  enrollmentId: string;
  session: string;
  setDoc: (doc: PortalSignableDocument) => void;
  reload: () => Promise<PortalSignableDocument | null>;
  onSignOut: () => void;
}

export interface DocumentSubmit {
  busy: boolean;
  notice: SubmitNotice | null;
  /** A 403: this session's role may not sign the document. */
  forbidden: boolean;
  submit: (input: SignatureInput, current: PortalSignableDocument) => void;
}

/**
 * `POST .../documents/{documentId}/sign` for the document sign page, echoing
 * the document's `consentVersion` exactly as the list returned it.
 *
 * Outcomes: success replaces the document with the signed response; 401
 * signs out; 403 sets `forbidden`; 409 (already signed) re-reads the list,
 * which then shows the signed state. A 422 re-reads the list too: a changed
 * `consentVersion` means the consent text changed, so the new text replaces
 * the old (the form clears its checkbox on the new version) and a warning
 * asks for re-confirmation; otherwise the signature image was rejected and
 * the member is asked to redraw it.
 */
export function useDocumentSubmit({
  enrollmentId,
  session,
  setDoc,
  reload,
  onSignOut,
}: UseDocumentSubmitArgs): DocumentSubmit {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<SubmitNotice | null>(null);
  const [forbidden, setForbidden] = useState(false);

  const handleSubmitError = async (
    err: unknown,
    current: PortalSignableDocument,
  ): Promise<void> => {
    const status = err instanceof ApiError ? err.status : null;
    if (status === 401) {
      onSignOut();
    } else if (status === 403) {
      setForbidden(true);
    } else if (status === 409) {
      await reload();
    } else if (status === 422) {
      const fresh = await reload();
      setNotice(
        fresh && fresh.consentVersion !== current.consentVersion
          ? {
              tone: "warning",
              title: "The authorization text was updated.",
              body: "Please read the updated statement next to the checkbox, then check the box to confirm it.",
            }
          : {
              tone: "error",
              title: "We couldn't read your signature.",
              body: "Clear the signature box, draw your signature again, and resubmit.",
            },
      );
    } else {
      setNotice({
        tone: "error",
        title: "We couldn't record your signature.",
        body: "Please try again. If it keeps happening, use Contact us to reach our team.",
      });
    }
  };

  const onSubmit = (input: SignatureInput, current: PortalSignableDocument): void => {
    setBusy(true);
    setNotice(null);
    apiFetch<PortalSignableDocument>(
      `${documentsApiPath(enrollmentId)}/${current.documentId}/sign`,
      {
        method: "POST",
        body: {
          ...input,
          intent: true,
          consentVersion: current.consentVersion,
        } satisfies PortalDocumentSignRequest,
      },
      { baseUrl: API_BASE, token: session },
    )
      .then(setDoc)
      .catch((err: unknown) => handleSubmitError(err, current))
      .finally(() => setBusy(false));
  };

  return { busy, notice, forbidden, submit: onSubmit };
}
